import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, FileText, Plus, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Step-1 modal shown before creating an invoice from scratch.
 * Forces the user to pick an existing quote OR create a new one first.
 *
 * Props:
 *  - show
 *  - projectId (optional pre-filter)
 *  - onClose()
 *  - onPickQuote(quote)  -> opens quote -> facture conversion
 *  - onCreateNewQuote()  -> opens InvoiceFormModal in 'devis' mode
 */
export const QuoteSelectorModal = ({ show, projectId = null, onClose, onPickQuote, onCreateNewQuote }) => {
  const [quotes, setQuotes] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!show) return;
    setLoading(true);
    const url = projectId
      ? `${API_URL}/api/invoicing/quotes?project_id=${projectId}`
      : `${API_URL}/api/invoicing/quotes`;
    axios.get(url, { withCredentials: true })
      .then(r => setQuotes((r.data || []).filter(q => q.status !== 'refuse')))
      .catch(() => toast.error('Erreur chargement devis'))
      .finally(() => setLoading(false));
  }, [show, projectId]);

  if (!show) return null;
  const s = (search || '').toLowerCase();
  const filtered = quotes.filter(q =>
    !s || (q.number || '').toLowerCase().includes(s) ||
    (q.client_name || '').toLowerCase().includes(s) ||
    (q.project_name || '').toLowerCase().includes(s)
  );

  return (
    <div data-testid="quote-selector-modal" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, overflowY: 'auto' }}>
      <div style={{ background: 'white', borderRadius: 10, width: '100%', maxWidth: 720, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Creer une facture</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }} data-testid="quote-selector-close"><X size={22} /></button>
        </div>
        <div style={{ padding: 18 }}>
          <p style={{ margin: '0 0 14px', color: '#5E5F66', fontSize: '0.88rem' }}>
            Une facture doit etre liee a un devis. Choisissez un devis existant ou creez-en un nouveau.
          </p>

          <button
            type="button"
            data-testid="create-new-quote-btn"
            onClick={onCreateNewQuote}
            style={{
              width: '100%', padding: '14px', borderRadius: 8,
              border: '2px dashed #1D4ED8', background: '#EFF6FF',
              color: '#1D4ED8', fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              marginBottom: 16, fontSize: '0.95rem',
            }}
          >
            <Plus size={20} weight="bold" /> Creer un nouveau devis
          </button>

          <div style={{ borderTop: '1px solid #E2E2E6', margin: '0 0 12px' }} />

          <div style={{ marginBottom: 10 }}>
            <input
              data-testid="quote-search-input"
              className="form-input"
              placeholder="Rechercher un devis (numero, client, chantier)..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ fontSize: '0.85rem' }}
            />
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 30, color: '#5E5F66' }}>Chargement...</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 30, color: '#5E5F66', fontSize: '0.88rem' }}>
              Aucun devis disponible. Cliquez sur "Creer un nouveau devis".
            </div>
          ) : (
            <div style={{ maxHeight: 360, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {filtered.map(q => {
                const total = (q.lines || []).reduce((s, ln) => s + (ln.unit_price || 0) * (ln.quantity || 0), 0);
                return (
                  <button
                    key={q.id}
                    type="button"
                    data-testid={`pick-quote-${q.id}`}
                    onClick={() => onPickQuote(q)}
                    style={{
                      width: '100%', textAlign: 'left', border: '1px solid #E2E2E6',
                      borderRadius: 8, background: 'white', padding: 12, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 12, transition: 'all 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#1D4ED8'; e.currentTarget.style.background = '#F8FAFF'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E2E2E6'; e.currentTarget.style.background = 'white'; }}
                  >
                    <FileText size={20} color="#1D4ED8" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: '0.92rem' }}>{q.number}</div>
                      <div style={{ fontSize: '0.78rem', color: '#5E5F66', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {q.client_name || 'Sans client'}{q.project_name ? ` - ${q.project_name}` : ''} - {q.date}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 700, color: '#047857' }}>{total.toFixed(2)} EUR HT</div>
                      <div style={{ fontSize: '0.72rem', color: '#5E5F66', textTransform: 'capitalize' }}>{q.status}</div>
                    </div>
                    <ArrowRight size={16} color="#9CA3AF" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteSelectorModal;
